import { memo } from 'react';

import { GetTripRes_stops } from '@/global-states/trip';
import { NewSocketMessageBus } from '@/socket';
import {
  getBusStopInfo,
  getStopVisualStates,
  stopBadgeClass,
  alphabet,
  getTimeFormatFromUnix,
} from '@/utils/utils';

const PositionCard = ({
  tripStops,
  busList,
  handleRefresh,
}: {
  tripStops?: GetTripRes_stops[];
  busList?: NewSocketMessageBus;
  handleRefresh: () => void;
}) => {
  const stopInfos = tripStops?.map((stop) => getBusStopInfo(busList, stop?.target_id, stop?.stop_index));

  // Shared with the map's stop markers: first blue, last red, in-between
  // gray, passed green.
  const stopStates = getStopVisualStates(tripStops, busList);

  return (
    <>
      <div className="mb-3 mt-5 flex items-center justify-between lg:mb-3.5 lg:mt-6">
        <span className="text-[15px] font-extrabold text-ro-black lg:text-base">
          Trip Details
        </span>
        <div className="flex items-center gap-2">
          {busList?.serverTimestamp ? (
            <span className="text-[11.5px] font-medium text-ro-stone lg:text-xs">
              Updated {getTimeFormatFromUnix(busList.serverTimestamp)}
            </span>
          ) : null}
          <button
            type="button"
            onClick={handleRefresh}
            aria-label="Refresh trip details"
            className="flex items-center gap-1.5 rounded-full border border-ro-border bg-white py-1 pl-2.5 pr-3 text-[11px] font-bold text-ro-black shadow-card transition-colors hover:bg-ro-light-bg active:scale-95"
          >
            <svg
              width="12"
              height="12"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2.6"
              strokeLinecap="round"
              strokeLinejoin="round"
              aria-hidden
            >
              <path d="M21 12a9 9 0 1 1-2.64-6.36" />
              <path d="M21 3v6h-6" />
            </svg>
            <span>Refresh</span>
          </button>
        </div>
      </div>

      <div className="rounded-2xl border border-ro-border-light bg-white px-4 pb-1 pt-4 shadow-card lg:px-[18px] lg:pt-[18px]">
        {tripStops?.map((tripStop, index) => {
          const info = stopInfos?.[index];
          const state = stopStates[index] ?? 'middle';
          const isLast = index === (tripStops?.length ?? 0) - 1;
          // Live bus data owns this slot entirely: with a bus list we show the
          // socket ETA or nothing at all. The trip API's scheduled
          // operation_timestamp only fills the pre-socket first paint. Both
          // are unix seconds, so the format is unchanged.
          const arrivalTimestamp = busList
            ? info?.etaTimestamp
            : tripStop.operation_timestamp;

          return (
            <div
              key={`trip_stop_${tripStop.stop_index}`}
              className="flex gap-3.5 lg:gap-[15px]"
            >
              <div className="flex w-[26px] shrink-0 flex-col items-center lg:w-7">
                <div className="relative h-[26px] w-[26px] shrink-0 lg:h-7 lg:w-7">
                  {state === 'last' ? (
                    <div className="absolute inset-[-3px] animate-ro-pulse rounded-full bg-ro-red opacity-40" />
                  ) : null}
                  <div
                    className={`absolute inset-0 flex items-center justify-center rounded-full text-xs font-extrabold lg:text-[13px] ${stopBadgeClass(
                      state
                    )}`}
                  >
                    {alphabet[Number(tripStop?.stop_index)]}
                  </div>
                </div>
                {!isLast ? (
                  <div
                    className={`mt-[3px] min-h-[30px] w-[2.5px] flex-1 rounded-sm lg:min-h-[34px] ${
                      state === 'passed' ? 'bg-ro-green' : 'bg-ro-divider'
                    }`}
                  />
                ) : null}
              </div>

              <div className="min-w-0 flex-1 pb-4 lg:pb-[18px]">
                <div className="flex items-start justify-between gap-2">
                  <div className="min-w-0">
                    <div
                      className={`text-sm leading-[1.3] lg:text-[15px] ${
                        state === 'passed'
                          ? 'font-semibold text-ro-muted'
                          : 'font-bold text-ro-black'
                      } ${state === 'last' ? 'font-extrabold' : ''}`}
                    >
                      {tripStop.name}
                    </div>
                    {tripStop.name !== tripStop.description && (
                      <div className="mt-0.5 text-xs font-medium leading-[1.35] text-ro-stone lg:text-[12.5px]">
                        {tripStop.description}
                      </div>
                    )}
                  </div>
                  {arrivalTimestamp ? (
                    <div className="shrink-0 text-right text-xs font-medium text-ro-stone lg:text-[12.5px]">
                      <div>Est. arrival</div>
                      <div>{getTimeFormatFromUnix(arrivalTimestamp)}</div>
                    </div>
                  ) : null}
                </div>
                {busList ? (
                  <div
                    className={`mt-1.5 inline-block rounded-full px-2 py-0.5 text-[10.5px] font-extrabold tracking-[0.05em] lg:px-[9px] lg:text-[11px] ${
                      info?.etaMinute
                        ? 'bg-ro-now-bg text-ro-yellow-dark'
                        : 'bg-ro-green-bg text-ro-green'
                    }`}
                  >
                    {info?.etaMinute ? `ETA ${info.etaMinute} MIN` : 'VISITED'}
                  </div>
                ) : null}
              </div>
            </div>
          );
        })}
      </div>
    </>
  );
};

export default memo(PositionCard);
